// Shapes of fisiohome-backend's /api/v1/admin/routing/* payloads (docs/schemas/schemas.yaml
// #/Routing*), camelised by the Rails proxy.

export const ROUTING_ENVS = ["DEV", "STAGING", "PROD"] as const;
export type RoutingEnv = (typeof ROUTING_ENVS)[number];

export const GLOBAL_MODES = [
	"ALL_HERE",
	"PER_LOCATION",
	"ALL_HAVERSINE",
] as const;
export type GlobalMode = (typeof GLOBAL_MODES)[number];

export const LOCATION_MODES = ["HERE", "HAVERSINE"] as const;
export type LocationMode = (typeof LOCATION_MODES)[number];

export interface RoutingEnvSettings {
	env: RoutingEnv;
	mode: GlobalMode;
	noLocationMode: LocationMode;
	/** false = no stored row, the default applies */
	modeStored: boolean;
	noLocationModeStored: boolean;
	updatedAt?: string | null;
}

export interface RoutingSettings {
	currentEnv: RoutingEnv;
	/** false = HERE_API_ENABLED=false on the backend; every search uses haversine */
	hereApiEnabled: boolean;
	envs: RoutingEnvSettings[];
}

/** A deferred prop read through the Go proxy. `error` is non-null only when the read failed. */
export interface ExternalProp<T> {
	data: T | null;
	/** pagination, for paged reads */
	meta?: RoutingPaginationMeta | null;
	error: string | null;
}

export const LOCATION_MODE_FILTERS = ["HERE", "HAVERSINE", "DEFAULT"] as const;
export type LocationModeFilter = (typeof LOCATION_MODE_FILTERS)[number];

export interface RoutingLocationPolicy {
	locationId: number;
	city: string;
	state: string;
	/** false = no policy row; the location is on the default (HERE) */
	configured: boolean;
	mode: LocationMode;
	circuityFactor: number | null;
	speedKmh: number | null;
	notes: string | null;
	updatedBy?: string | null;
	updatedAt?: string | null;
	/** false = no circuity_factor yet; haversine would use the conservative default 1.4 */
	calibrated: boolean;
	/** what a search for this location uses right now in the backend's own env */
	effectiveMode: LocationMode;
	effectiveSource:
		| "env_disabled"
		| "global"
		| "no_location"
		| "location"
		| "default";
	visits90d: number;
}

export const CALIBRATION_FILTERS = ["CALIBRATED", "UNCALIBRATED"] as const;
export type CalibrationFilter = (typeof CALIBRATION_FILTERS)[number];

/** Applied in the browser: the backend returns every location in one list. "" = all. */
export interface RoutingLocationFilters {
	state: string;
	city: string;
	mode: LocationModeFilter | "";
	calibration: CalibrationFilter | "";
}

export interface RoutingPolicyChange {
	id: number;
	scope: "GLOBAL" | "NO_LOCATION" | "LOCATION";
	// Go omits these when empty (omitempty), so they arrive absent rather than null.
	/** set for GLOBAL and NO_LOCATION */
	env?: RoutingEnv;
	locationId?: number;
	/** snapshot at the time of the change */
	locationCity?: string;
	action: "SET" | "RESET";
	/** null when nothing was set before */
	oldValue: Record<string, unknown> | null;
	/** null for RESET */
	newValue: Record<string, unknown> | null;
	reason?: string;
	changedBy: string;
	changedByEmail: string | null;
	actorSource: "jwt" | "service_performed_by" | "service_bound_user";
	/** UTC */
	changedAt: string;
}

export interface RoutingPaginationMeta {
	page?: number;
	limit?: number;
	totalItems?: number;
	totalPages?: number;
}

export interface RoutingHistoryFilters {
	locationId?: number;
	changedBy?: string;
	from?: string;
	to?: string;
}

export interface RoutingHistoryOptions {
	locations: { id: number; city: string; state: string }[];
	admins: { userId: string; name: string; email: string | null }[];
}
